<script setup>
import { computed, onMounted, ref } from 'vue';
import { endOfDay, getUnixTime, startOfDay, subDays } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useAccount } from 'dashboard/composables/useAccount';
import ReportsAPI from 'dashboard/api/reports';
import LiveReportsAPI from 'dashboard/api/liveReports';
import ReportHeader from './components/ReportHeader.vue';
import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';
import SearchInboxSelector from 'dashboard/modules/search/components/SearchInboxSelector.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t, locale } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

const filters = ref({
  inboxId: null,
  dateRange: {
    type: 'last_30_days',
    from: getUnixTime(startOfDay(subDays(new Date(), 30))),
    to: getUnixTime(endOfDay(new Date())),
  },
});

const isLiveLoading = ref(false);
const isPeriodLoading = ref(false);
const lastLiveUpdateAt = ref(null);
const lastPeriodUpdateAt = ref(null);

const liveSummary = ref({
  open: 0,
  unattended: 0,
  unassigned: 0,
  pending: 0,
  agents: [],
  teams: [],
});

const periodSummary = ref({
  noResponse: {
    totalCount: 0,
    awaitingAgentCount: 0,
    firstReplyPendingCount: 0,
    awaitingCustomerCount: 0,
    rows: [],
  },
  closing: {
    totalCount: 0,
    categories: [],
    types: [],
  },
  reopen: {
    totalCount: 0,
    averageReopenTimeSeconds: 0,
    rows: [],
  },
  reschedule: {
    totalCount: 0,
    pendingCount: 0,
    sentCount: 0,
    rows: [],
  },
  whatsapp: {
    totalCount: 0,
    totalTemplatesCount: 0,
    templates: [],
  },
  contactReasons: {
    totalCount: 0,
    classifiedCount: 0,
    unclassifiedCount: 0,
    rows: [],
  },
});

const localeCode = computed(() => locale.value?.replace('_', '-') || 'es');

const payload = computed(() => ({
  from: filters.value.dateRange?.from,
  to: filters.value.dateRange?.to,
  inboxId: filters.value.inboxId,
}));

const detailRoutes = computed(() => ({
  noResponse: accountScopedRoute('no_response_reports'),
  closing: accountScopedRoute('closing_reports'),
  reopens: accountScopedRoute('reopen_reports'),
  reschedules: accountScopedRoute('reschedule_reports'),
  whatsapp: accountScopedRoute('whatsapp_template_reports'),
  contactReasons: accountScopedRoute('contact_reason_reports'),
  overview: accountScopedRoute('account_overview_reports'),
}));

const formatDateTime = value => {
  if (!value) return '--';

  return new Intl.DateTimeFormat(localeCode.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatDuration = value => {
  const seconds = Number(value || 0);
  if (!seconds) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86_400).toFixed(1)} d`;
};

const formatRefreshTime = value => {
  if (!value) return t('SUPERVISOR_DASHBOARD.NOT_UPDATED');

  return formatDateTime(value);
};

const responseStateLabel = state =>
  t(`NO_RESPONSE_REPORTS.STATES.${String(state).toUpperCase()}`, state);

const rescheduleStatusLabel = status =>
  t(`RESCHEDULE_REPORTS.STATUSES.${String(status).toUpperCase()}`, status);

const findAgent = id =>
  agents.value.find(agent => Number(agent.id) === Number(id));
const findTeam = id => teams.value.find(team => Number(team.id) === Number(id));

const liveAgentRows = computed(() =>
  (liveSummary.value.agents || [])
    .map(row => ({
      id: row.assignee_id,
      label:
        findAgent(row.assignee_id)?.available_name ||
        findAgent(row.assignee_id)?.name ||
        t('SUPERVISOR_DASHBOARD.UNASSIGNED_AGENT'),
      open: row.open || 0,
      unattended: row.unattended || 0,
      unassigned: row.unassigned || 0,
    }))
    .sort((a, b) => b.open - a.open || b.unattended - a.unattended)
    .slice(0, 5)
);

const liveTeamRows = computed(() =>
  (liveSummary.value.teams || [])
    .map(row => ({
      id: row.team_id,
      label: findTeam(row.team_id)?.name || t('SUPERVISOR_DASHBOARD.NO_TEAM'),
      open: row.open || 0,
      unattended: row.unattended || 0,
      unassigned: row.unassigned || 0,
    }))
    .sort((a, b) => b.open - a.open || b.unattended - a.unattended)
    .slice(0, 5)
);

const topNoResponseRows = computed(() =>
  [...(periodSummary.value.noResponse.rows || [])]
    .sort((a, b) => Number(b.idle_seconds || 0) - Number(a.idle_seconds || 0))
    .slice(0, 6)
);

const topClosingCategory = computed(
  () => periodSummary.value.closing.categories?.[0]?.label || '--'
);
const topReasonRows = computed(() =>
  [...(periodSummary.value.contactReasons.rows || [])]
    .sort((a, b) => Number(b.count || 0) - Number(a.count || 0))
    .slice(0, 5)
);

const topTemplateRows = computed(() =>
  [...(periodSummary.value.whatsapp.templates || [])]
    .sort((a, b) => Number(b.total_count || 0) - Number(a.total_count || 0))
    .slice(0, 5)
);

const recentReopenRows = computed(() =>
  [...(periodSummary.value.reopen.rows || [])].slice(0, 5)
);

const recentRescheduleRows = computed(() =>
  [...(periodSummary.value.reschedule.rows || [])].slice(0, 5)
);

const fetchLiveData = async () => {
  try {
    isLiveLoading.value = true;
    const [metricsResult, agentsResult, teamsResult] = await Promise.allSettled(
      [
        LiveReportsAPI.getConversationMetric(),
        LiveReportsAPI.getGroupedConversations({ groupBy: 'assignee_id' }),
        LiveReportsAPI.getGroupedConversations({ groupBy: 'team_id' }),
      ]
    );

    liveSummary.value = {
      open:
        metricsResult.status === 'fulfilled'
          ? metricsResult.value.data.open || 0
          : 0,
      unattended:
        metricsResult.status === 'fulfilled'
          ? metricsResult.value.data.unattended || 0
          : 0,
      unassigned:
        metricsResult.status === 'fulfilled'
          ? metricsResult.value.data.unassigned || 0
          : 0,
      pending:
        metricsResult.status === 'fulfilled'
          ? metricsResult.value.data.pending || 0
          : 0,
      agents:
        agentsResult.status === 'fulfilled'
          ? agentsResult.value.data || []
          : [],
      teams:
        teamsResult.status === 'fulfilled' ? teamsResult.value.data || [] : [],
    };
    lastLiveUpdateAt.value = new Date();
  } finally {
    isLiveLoading.value = false;
  }
};

const fetchPeriodData = async () => {
  try {
    isPeriodLoading.value = true;
    const [
      noResponseResult,
      closingResult,
      reopenResult,
      rescheduleResult,
      whatsappResult,
      contactReasonsResult,
    ] = await Promise.allSettled([
      ReportsAPI.getNoResponseSummary(payload.value),
      ReportsAPI.getClosingSummary(payload.value),
      ReportsAPI.getReopenSummary(payload.value),
      ReportsAPI.getRescheduleSummary(payload.value),
      ReportsAPI.getWhatsappTemplateSummary(payload.value),
      ReportsAPI.getContactReasonSummary(payload.value),
    ]);

    periodSummary.value = {
      noResponse:
        noResponseResult.status === 'fulfilled'
          ? {
              totalCount: noResponseResult.value.data.total_count || 0,
              awaitingAgentCount:
                noResponseResult.value.data.awaiting_agent_count || 0,
              firstReplyPendingCount:
                noResponseResult.value.data.first_reply_pending_count || 0,
              awaitingCustomerCount:
                noResponseResult.value.data.awaiting_customer_count || 0,
              rows: noResponseResult.value.data.rows || [],
            }
          : {
              totalCount: 0,
              awaitingAgentCount: 0,
              firstReplyPendingCount: 0,
              awaitingCustomerCount: 0,
              rows: [],
            },
      closing:
        closingResult.status === 'fulfilled'
          ? {
              totalCount: closingResult.value.data.total_count || 0,
              categories: closingResult.value.data.categories || [],
              types: closingResult.value.data.types || [],
            }
          : {
              totalCount: 0,
              categories: [],
              types: [],
            },
      reopen:
        reopenResult.status === 'fulfilled'
          ? {
              totalCount: reopenResult.value.data.total_count || 0,
              averageReopenTimeSeconds:
                reopenResult.value.data.average_reopen_time_seconds || 0,
              rows: reopenResult.value.data.rows || [],
            }
          : {
              totalCount: 0,
              averageReopenTimeSeconds: 0,
              rows: [],
            },
      reschedule:
        rescheduleResult.status === 'fulfilled'
          ? {
              totalCount: rescheduleResult.value.data.total_count || 0,
              pendingCount: rescheduleResult.value.data.pending_count || 0,
              sentCount: rescheduleResult.value.data.sent_count || 0,
              rows: rescheduleResult.value.data.rows || [],
            }
          : {
              totalCount: 0,
              pendingCount: 0,
              sentCount: 0,
              rows: [],
            },
      whatsapp:
        whatsappResult.status === 'fulfilled'
          ? {
              totalCount: whatsappResult.value.data.total_count || 0,
              totalTemplatesCount:
                whatsappResult.value.data.total_templates_count || 0,
              templates: whatsappResult.value.data.templates || [],
            }
          : {
              totalCount: 0,
              totalTemplatesCount: 0,
              templates: [],
            },
      contactReasons:
        contactReasonsResult.status === 'fulfilled'
          ? {
              totalCount: contactReasonsResult.value.data.total_count || 0,
              classifiedCount:
                contactReasonsResult.value.data.classified_count || 0,
              unclassifiedCount:
                contactReasonsResult.value.data.unclassified_count || 0,
              rows: contactReasonsResult.value.data.rows || [],
            }
          : {
              totalCount: 0,
              classifiedCount: 0,
              unclassifiedCount: 0,
              rows: [],
            },
    };
    lastPeriodUpdateAt.value = new Date();
  } finally {
    isPeriodLoading.value = false;
  }
};

const fetchAll = async () => {
  await Promise.all([fetchLiveData(), fetchPeriodData()]);
};

const { startRefetching: startLiveRefresh } = useLiveRefresh(
  fetchLiveData,
  60_000
);
const { startRefetching: startPeriodRefresh } = useLiveRefresh(
  fetchPeriodData,
  180_000
);

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  await fetchAll();
  startLiveRefresh();
  startPeriodRefresh();
});
</script>

<template>
  <div class="flex flex-col gap-5 pb-8">
    <ReportHeader
      :header-title="$t('SUPERVISOR_DASHBOARD.HEADER')"
      :header-description="$t('SUPERVISOR_DASHBOARD.DESCRIPTION')"
    >
      <NextButton
        slate
        icon="i-lucide-refresh-cw"
        :label="$t('SUPERVISOR_DASHBOARD.REFRESH')"
        :is-loading="isLiveLoading || isPeriodLoading"
        @click="fetchAll"
      />
    </ReportHeader>

    <div
      class="chatmigo-report-filter-card rounded-xl border border-n-weak bg-n-alpha-1 p-4"
    >
      <div
        class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
      >
        <div
          class="flex flex-col lg:flex-row items-start lg:items-center gap-3"
        >
          <SearchDateRangeSelector
            v-model="filters.dateRange"
            class="min-w-0 max-w-full"
            @change="fetchPeriodData"
          />
          <div class="hidden h-4 w-px flex-shrink-0 bg-n-weak lg:block" />
          <SearchInboxSelector
            v-model="filters.inboxId"
            :label="$t('SUPERVISOR_DASHBOARD.INBOX_FILTER')"
            @change="fetchPeriodData"
          />
        </div>
        <div class="text-xs text-n-slate-11">
          <div class="mb-1 flex flex-wrap items-center justify-end gap-2">
            <span class="chatmigo-report-chip chatmigo-report-chip--success">
              <span class="size-1.5 rounded-full bg-emerald-10 animate-pulse" />
              {{ $t('SUPERVISOR_DASHBOARD.REALTIME.BADGE') }}
            </span>
            <span class="chatmigo-report-chip">
              {{ $t('SUPERVISOR_DASHBOARD.REALTIME.LIVE') }}
            </span>
            <span class="chatmigo-report-chip">
              {{ $t('SUPERVISOR_DASHBOARD.REALTIME.PERIOD') }}
            </span>
          </div>
          <p class="m-0">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_UPDATED') }}:
            {{ formatRefreshTime(lastLiveUpdateAt) }}
          </p>
          <p class="m-0">
            {{ $t('SUPERVISOR_DASHBOARD.PERIOD_UPDATED') }}:
            {{ formatRefreshTime(lastPeriodUpdateAt) }}
          </p>
        </div>
      </div>
    </div>

    <section class="flex flex-col gap-4">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h3 class="mb-1 text-base font-semibold text-n-slate-12">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.TITLE') }}
          </h3>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.DESCRIPTION') }}
          </p>
        </div>
        <router-link
          class="text-sm font-medium text-n-brand hover:underline"
          :to="detailRoutes.overview"
        >
          {{ $t('SUPERVISOR_DASHBOARD.VIEW_DETAIL') }}
        </router-link>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div
          class="chatmigo-report-card chatmigo-report-metric-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <p class="mb-1 text-sm text-n-slate-11">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.OPEN') }}
          </p>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">
            {{ liveSummary.open }}
          </p>
        </div>
        <div
          class="chatmigo-report-card chatmigo-report-metric-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <p class="mb-1 text-sm text-n-slate-11">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.UNATTENDED') }}
          </p>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">
            {{ liveSummary.unattended }}
          </p>
        </div>
        <div
          class="chatmigo-report-card chatmigo-report-metric-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <p class="mb-1 text-sm text-n-slate-11">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.UNASSIGNED') }}
          </p>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">
            {{ liveSummary.unassigned }}
          </p>
        </div>
        <div
          class="chatmigo-report-card chatmigo-report-metric-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <p class="mb-1 text-sm text-n-slate-11">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.PENDING') }}
          </p>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">
            {{ liveSummary.pending }}
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section
          class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <h4 class="mb-4 text-base font-semibold text-n-slate-12">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.BY_AGENT') }}
          </h4>
          <div class="chatmigo-report-table-shell overflow-x-auto px-4">
            <table class="chatmigo-report-table text-sm">
              <thead>
                <tr class="text-left">
                  <th class="py-2 pr-3">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.AGENT') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.OPEN') }}
                  </th>
                  <th class="py-2">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.UNATTENDED') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in liveAgentRows"
                  :key="`agent-${row.id}`"
                  class="chatmigo-report-row"
                >
                  <td class="py-2 pr-3 text-n-slate-12">{{ row.label }}</td>
                  <td class="py-2 pr-3 text-n-slate-12">{{ row.open }}</td>
                  <td class="py-2 text-n-slate-12">{{ row.unattended }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <h4 class="mb-4 text-base font-semibold text-n-slate-12">
            {{ $t('SUPERVISOR_DASHBOARD.LIVE_SECTION.BY_TEAM') }}
          </h4>
          <div class="chatmigo-report-table-shell overflow-x-auto px-4">
            <table class="chatmigo-report-table text-sm">
              <thead>
                <tr class="text-left">
                  <th class="py-2 pr-3">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.TEAM') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.OPEN') }}
                  </th>
                  <th class="py-2">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.UNATTENDED') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in liveTeamRows"
                  :key="`team-${row.id}`"
                  class="chatmigo-report-row"
                >
                  <td class="py-2 pr-3 text-n-slate-12">{{ row.label }}</td>
                  <td class="py-2 pr-3 text-n-slate-12">{{ row.open }}</td>
                  <td class="py-2 text-n-slate-12">{{ row.unattended }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>

    <section class="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <section
        class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 class="mb-1 text-base font-semibold text-n-slate-12">
              {{ $t('SUPERVISOR_DASHBOARD.ALERT_SECTION.TITLE') }}
            </h3>
            <p class="m-0 text-sm text-n-slate-11">
              {{ $t('SUPERVISOR_DASHBOARD.ALERT_SECTION.DESCRIPTION') }}
            </p>
          </div>
          <router-link
            class="text-sm font-medium text-n-brand hover:underline"
            :to="detailRoutes.noResponse"
          >
            {{ $t('SUPERVISOR_DASHBOARD.VIEW_DETAIL') }}
          </router-link>
        </div>

        <div class="mb-4 grid grid-cols-2 gap-3">
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('NO_RESPONSE_REPORTS.AWAITING_AGENT') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.noResponse.awaitingAgentCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('NO_RESPONSE_REPORTS.FIRST_REPLY_PENDING') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.noResponse.firstReplyPendingCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('NO_RESPONSE_REPORTS.AWAITING_CUSTOMER') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.noResponse.awaitingCustomerCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('NO_RESPONSE_REPORTS.TOTAL') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.noResponse.totalCount }}
            </p>
          </div>
        </div>

        <div class="chatmigo-report-table-shell overflow-x-auto px-4">
          <table class="chatmigo-report-table text-sm">
            <thead>
              <tr class="text-left">
                <th class="py-2 pr-3">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.STATE') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.CONVERSATION') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.INBOX') }}
                </th>
                <th class="py-2">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.IDLE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in topNoResponseRows"
                :key="`no-response-${row.id}`"
                class="chatmigo-report-row"
              >
                <td class="py-2 pr-3 text-n-slate-12">
                  {{ responseStateLabel(row.response_state) }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">
                  #{{ row.conversation_display_id }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.inbox_name }}</td>
                <td class="py-2 text-n-slate-12">
                  {{ formatDuration(row.idle_seconds) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 class="mb-1 text-base font-semibold text-n-slate-12">
              {{ $t('SUPERVISOR_DASHBOARD.CLOSING_SECTION.TITLE') }}
            </h3>
            <p class="m-0 text-sm text-n-slate-11">
              {{ $t('SUPERVISOR_DASHBOARD.CLOSING_SECTION.DESCRIPTION') }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <router-link
              class="text-sm font-medium text-n-brand hover:underline"
              :to="detailRoutes.closing"
            >
              {{ $t('SUPERVISOR_DASHBOARD.CLOSING_SECTION.CLOSINGS_LINK') }}
            </router-link>
            <router-link
              class="text-sm font-medium text-n-brand hover:underline"
              :to="detailRoutes.reopens"
            >
              {{ $t('SUPERVISOR_DASHBOARD.CLOSING_SECTION.REOPENS_LINK') }}
            </router-link>
          </div>
        </div>

        <div class="mb-4 grid grid-cols-2 gap-3">
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('CLOSING_REPORTS.TOTAL_CLOSED') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.closing.totalCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('REOPEN_REPORTS.TOTAL_REOPENS') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.reopen.totalCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('SUPERVISOR_DASHBOARD.CLOSING_SECTION.TOP_CATEGORY') }}
            </p>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ topClosingCategory }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{
                $t('SUPERVISOR_DASHBOARD.CLOSING_SECTION.AVERAGE_REOPEN_TIME')
              }}
            </p>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{
                formatDuration(periodSummary.reopen.averageReopenTimeSeconds)
              }}
            </p>
          </div>
        </div>

        <div class="chatmigo-report-table-shell overflow-x-auto px-4">
          <table class="chatmigo-report-table text-sm">
            <thead>
              <tr class="text-left">
                <th class="py-2 pr-3">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.DATE') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.CONVERSATION') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.INBOX') }}
                </th>
                <th class="py-2">
                  {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.REOPEN_DELAY') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in recentReopenRows"
                :key="`reopen-${row.id}`"
                class="chatmigo-report-row"
              >
                <td class="py-2 pr-3 text-n-slate-12">
                  {{ formatDateTime(row.reopened_at) }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">
                  #{{ row.conversation_display_id }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.inbox_name }}</td>
                <td class="py-2 text-n-slate-12">
                  {{ formatDuration(row.time_since_resolution_seconds) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>

    <section class="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <section
        class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 class="mb-1 text-base font-semibold text-n-slate-12">
              {{ $t('SUPERVISOR_DASHBOARD.WHATSAPP_SECTION.TITLE') }}
            </h3>
            <p class="m-0 text-sm text-n-slate-11">
              {{ $t('SUPERVISOR_DASHBOARD.WHATSAPP_SECTION.DESCRIPTION') }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <router-link
              class="text-sm font-medium text-n-brand hover:underline"
              :to="detailRoutes.reschedules"
            >
              {{ $t('SUPERVISOR_DASHBOARD.WHATSAPP_SECTION.RESCHEDULES_LINK') }}
            </router-link>
            <router-link
              class="text-sm font-medium text-n-brand hover:underline"
              :to="detailRoutes.whatsapp"
            >
              {{ $t('SUPERVISOR_DASHBOARD.WHATSAPP_SECTION.TEMPLATES_LINK') }}
            </router-link>
          </div>
        </div>

        <div class="mb-4 grid grid-cols-2 gap-3">
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('RESCHEDULE_REPORTS.PENDING') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.reschedule.pendingCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('RESCHEDULE_REPORTS.SENT') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.reschedule.sentCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATE_REPORTS.TOTAL_MESSAGES') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.whatsapp.totalCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATE_REPORTS.TOTAL_TEMPLATES') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.whatsapp.totalTemplatesCount }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div class="chatmigo-report-table-shell overflow-x-auto px-4">
            <table class="chatmigo-report-table text-sm">
              <thead>
                <tr class="text-left">
                  <th class="py-2 pr-3">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.TEMPLATE') }}
                  </th>
                  <th class="py-2">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.COUNT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in topTemplateRows"
                  :key="`template-${row.template_name}-${row.template_language}`"
                  class="chatmigo-report-row"
                >
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ row.template_name }}
                  </td>
                  <td class="py-2 text-n-slate-12">{{ row.total_count }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="chatmigo-report-table-shell overflow-x-auto px-4">
            <table class="chatmigo-report-table text-sm">
              <thead>
                <tr class="text-left">
                  <th class="py-2 pr-3">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.STATUS') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.TEMPLATE') }}
                  </th>
                  <th class="py-2">
                    {{ $t('SUPERVISOR_DASHBOARD.COLUMNS.SCHEDULED_AT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in recentRescheduleRows"
                  :key="`reschedule-${row.id}`"
                  class="chatmigo-report-row"
                >
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ rescheduleStatusLabel(row.status) }}
                  </td>
                  <td class="py-2 pr-3 text-n-slate-12">
                    {{ row.template_name }}
                  </td>
                  <td class="py-2 text-n-slate-12">
                    {{ formatDateTime(row.scheduled_at || row.sent_at) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section
        class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 class="mb-1 text-base font-semibold text-n-slate-12">
              {{ $t('SUPERVISOR_DASHBOARD.REASON_SECTION.TITLE') }}
            </h3>
            <p class="m-0 text-sm text-n-slate-11">
              {{ $t('SUPERVISOR_DASHBOARD.REASON_SECTION.DESCRIPTION') }}
            </p>
          </div>
          <router-link
            class="text-sm font-medium text-n-brand hover:underline"
            :to="detailRoutes.contactReasons"
          >
            {{ $t('SUPERVISOR_DASHBOARD.VIEW_DETAIL') }}
          </router-link>
        </div>

        <div class="mb-4 grid grid-cols-3 gap-3">
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('CONTACT_REASON_REPORTS.TOTAL') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.contactReasons.totalCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('CONTACT_REASON_REPORTS.CLASSIFIED') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.contactReasons.classifiedCount }}
            </p>
          </div>
          <div class="chatmigo-report-metric-card p-3">
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('CONTACT_REASON_REPORTS.UNCLASSIFIED') }}
            </p>
            <p class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ periodSummary.contactReasons.unclassifiedCount }}
            </p>
          </div>
        </div>

        <div class="chatmigo-report-table-shell overflow-x-auto px-4">
          <table class="chatmigo-report-table text-sm">
            <thead>
              <tr class="text-left">
                <th class="py-2 pr-3">
                  {{ $t('CONTACT_REASON_REPORTS.COLUMNS.CATEGORY') }}
                </th>
                <th class="py-2 pr-3">
                  {{ $t('CONTACT_REASON_REPORTS.COLUMNS.REASON') }}
                </th>
                <th class="py-2">
                  {{ $t('CONTACT_REASON_REPORTS.COLUMNS.COUNT') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in topReasonRows"
                :key="`reason-${row.category_name}-${row.reason_name}`"
                class="chatmigo-report-row"
              >
                <td class="py-2 pr-3 text-n-slate-12">
                  {{ row.category_name }}
                </td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.reason_name }}</td>
                <td class="py-2 text-n-slate-12">{{ row.count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  </div>
</template>
